import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Video } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/seo";
import { SlotPicker, fmtSlot } from "@/components/booking/SlotPicker";
import { CalendarButton } from "@/components/meeting/CalendarButton";

export const BookingFlow = ({ guest, initial = {}, onBooked }) => {
  const [slot, setSlot] = useState("");
  const [f, setF] = useState({ name: initial.name || "", email: initial.email || "", phone: "", business_name: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [reload, setReload] = useState(0);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const book = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/api/booking/book", { start_at: slot, ...(guest ? f : { notes: f.notes }) }); setDone(data); onBooked?.(data); }
    catch (err) { toast.error(errMsg(err, "Couldn't book")); if (err.response?.status === 409) { setSlot(""); setReload(r => r + 1); } } finally { setBusy(false); }
  };
  if (done) return (
    <div className="text-center py-4" data-testid="booking-success">
      <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
      <p className="font-bold text-lg text-slate-900">You're booked!</p>
      <p className="text-slate-600 mb-4">{fmtSlot(done.booking.start_at)} — confirmation sent to {done.booking.email}.</p>
      <div className="flex flex-wrap justify-center gap-2">
        <CalendarButton c={{ id: done.conference_id, title: "ShopAdvisor onboarding call", start_at: done.booking.start_at, duration: done.booking.minutes }} />
        {!guest && <Link to={`/conference/${done.conference_id}`} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-1" data-testid="booking-open-meeting"><Video className="w-4 h-4" />Meeting room</Link>}
      </div>
    </div>
  );
  return (
    <form onSubmit={book} className="space-y-4" data-testid="booking-flow">
      <SlotPicker value={slot} onPick={setSlot} reloadKey={reload} />
      {slot && <>
        <p className="text-sm font-semibold text-slate-900" data-testid="booking-selected">Selected: {fmtSlot(slot)}</p>
        {guest && <div className="grid sm:grid-cols-2 gap-3">
          <Input required maxLength={100} placeholder="Your name *" value={f.name} onChange={set("name")} data-testid="booking-name-input" />
          <Input required maxLength={200} type="email" placeholder="Email *" value={f.email} onChange={set("email")} data-testid="booking-email-input" />
          <Input maxLength={30} type="tel" placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="booking-phone-input" />
          <Input maxLength={150} placeholder="Shop name" value={f.business_name} onChange={set("business_name")} data-testid="booking-business-input" />
        </div>}
        <Textarea maxLength={1500} placeholder="Anything we should know before the call? (optional)" value={f.notes} onChange={set("notes")} data-testid="booking-notes-input" />
        <button disabled={busy} className="w-full bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold py-3 rounded-xl flex items-center justify-center gap-2" data-testid="booking-confirm-btn">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Confirm my call</button>
      </>}
    </form>
  );
};

export default function BookPage() {
  usePageMeta("Book a Free Demo Call", "Pick a time for a free 30-minute ShopAdvisor demo and onboarding call for your auto repair shop.");
  const [q] = useSearchParams();
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-slate-50 pt-28 pb-20 px-6" data-testid="book-page">
      <div className="max-w-2xl mx-auto">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-blue-600 mb-3">Free demo & onboarding</p>
        <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-3">Pick a time that works</h1>
        <p className="text-slate-500 mb-8">A friendly 30-minute video call — we'll walk through ShopAdvisor, review your website and map out your setup. No pressure.</p>
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm"><BookingFlow guest={!user || user.role !== "customer"} initial={{ name: q.get("name"), email: q.get("email") }} /></div>
      </div>
    </div>
  );
}
